import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export interface WelcomePointsPhaseStyleOptions {
  scrollContentPaddingTop: number;
}

export const getWelcomePointsPhaseStyles = (
  theme: Theme,
  options: WelcomePointsPhaseStyleOptions,
) =>
  StyleSheet.create({
    phaseRoot: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
        flexBasis: 0 as never,
      }),
    },
    scrollPhase: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    scrollPhaseWeb: {
      height: '100%',
      ...(Platform.OS === 'web' && {
        overscrollBehavior: 'contain' as never,
        overscrollBehaviorY: 'contain' as never,
      }),
    },
    scrollContent: {
      paddingHorizontal: theme.spacing.md,
      paddingTop: options.scrollContentPaddingTop,
      paddingBottom: theme.spacing.lg,
      ...(Platform.OS === 'web' && {
        maxWidth: 520,
        width: '100%',
        alignSelf: 'center' as const,
      }),
    },
    phaseLabel: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 2,
      textTransform: 'uppercase',
      marginBottom: theme.spacing.xs,
      color: theme.accentDark,
    },
    phaseTitle: {
      fontSize: theme.typography.h2.fontSize,
      fontWeight: '800',
      color: theme.black,
      marginBottom: theme.spacing.md,
    },
    timelineContainer: {
      position: 'relative',
      marginTop: theme.spacing.sm,
      paddingVertical: theme.spacing.md,
      marginBottom: theme.spacing.md,
    },
    timelineLine: {
      position: 'absolute',
      left: '50%',
      width: 3,
      marginLeft: -1.5,
      top: theme.spacing.lg,
      bottom: theme.spacing.lg,
      borderRadius: 2,
      backgroundColor: theme.primaryLight,
      zIndex: 0,
    },
    stageRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
      zIndex: 1,
    },
    halfColumn: {
      flex: 1,
      paddingHorizontal: theme.spacing.sm,
    },
    halfColumnLeft: {
      alignItems: 'flex-end',
    },
    halfColumnRight: {
      alignItems: 'flex-start',
    },
    centerTrack: {
      width: 12,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
    },
    timelineDot: {
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.primaryLight,
    },
    stageCard: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      maxWidth: 168,
      backgroundColor: `${theme.primary}24`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.sm + 2,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: `${theme.primary}55`,
      ...(Platform.OS === 'web'
        ? ({ boxShadow: `0 1px 2px ${theme.shadow}` } as never)
        : {
            shadowColor: theme.shadow,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.12,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    stageCardIconWrap: {
      marginRight: theme.spacing.sm,
      justifyContent: 'center',
      alignItems: 'center',
    },
    stageCardBody: {
      flex: 1,
      justifyContent: 'center',
    },
    stageCardTitle: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.black,
      marginBottom: 2,
    },
    pointsLine: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 1,
      gap: 4,
    },
    pointsLineLast: {
      marginBottom: 0,
    },
    pointsRowCompact: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
    },
    pointsStatGroup: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    pointsText: {
      fontSize: 11,
      fontWeight: '500',
      color: theme.gray[700],
    },
    pointsTextGolden: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.accentDark,
    },
    proTipsSection: {
      marginBottom: theme.spacing.sm,
    },
    proTipsHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.sm + 2,
    },
    proTipsTitle: {
      fontSize: theme.typography.h3.fontSize,
      fontWeight: '700',
      color: theme.black,
    },
    proTipCard: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.background.card,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.primaryLight,
      borderStyle: 'dashed',
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.md,
      marginBottom: theme.spacing.sm + 2,
      gap: theme.spacing.sm,
    },
    proTipIconsColumn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    proTipText: {
      flex: 1,
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 18,
      color: theme.gray[700],
    },
    proTipBold: {
      fontWeight: '700',
      color: theme.black,
    },
    footer: {
      paddingHorizontal: theme.spacing.md,
      paddingTop: theme.spacing.md,
      paddingBottom: theme.spacing.md,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[200] + '85',
      backgroundColor: theme.background.primary,
      ...(Platform.OS === 'web' && {
        maxWidth: 520,
        width: '100%',
        alignSelf: 'center' as const,
      }),
    },
    ctaButton: {
      paddingVertical: theme.spacing.md,
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: theme.spacing.sm,
      backgroundColor: theme.primary,
      ...(Platform.OS === 'web'
        ? ({ boxShadow: `0 0 20px ${theme.primary}4D` } as never)
        : {
            shadowColor: theme.primary,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.35,
            shadowRadius: 12,
            elevation: 6,
          }),
    },
    ctaButtonPressed: {
      opacity: 0.88,
      transform: [{ scale: 0.98 }],
    },
    ctaButtonHovered: {
      opacity: 0.9,
    },
    ctaButtonLabel: {
      fontSize: theme.typography.label.fontSize,
      fontWeight: '700',
      color: theme.onPrimary,
    },
  });
